import { CreatorBrand } from 'constants/brands';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import React, { ComponentType } from 'react';
import {
    Nav,
    MusicNote,
    SearchOutlined,
    ProductPluginsOutlined,
    useBreakpoint,
    ArchiveOutlined,
    Text,
    Box,
    Divider,
    HeadphonesAddOutlined,
    CaretRightOutlined,
    IconProps,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { RoutePaths } from 'types/enums';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { getIsCurrentPath } from 'helpers';
import { CONFIG } from 'utils/config';
import { useAppSettings } from 'contexts/AppSettingsContext/AppSettingsContext';
import { useLocation } from 'react-router-dom';
import { useGetBasename } from 'hooks/useGetBasename/useGetBasename';
import { NavigationDropdownItem } from '../components/NavigationDropdownItem';
export interface DropdownItemsProps {
    path: string;
    title: string;
    icon: ComponentType<IconProps>;
    isBrowserOnly?: boolean;
    target?: React.HTMLAttributeAnchorTarget;
    subTitle: string;
}

type Route = {
    path: string;
    text: string;
    icon: ComponentType<IconProps>;
    isBrowserOnly?: boolean;
    target?: React.HTMLAttributeAnchorTarget;
    pluginsDropdownItemsPart1?: Array<DropdownItemsProps>;
    pluginsDropdownItemsPart2?: Array<DropdownItemsProps>;
};

export const usePublicNavigation = ({
    isDAWPlugin,
}: {
    isDAWPlugin: boolean;
}) => {
    const { isAuthenticated } = useAuthentication();
    const basename = useGetBasename();
    const { showLandrPlugins } = useAppSettings();
    const isToolActive =
        useLocation().pathname.includes(RoutePaths.KeyBpmFinder) ||
        useLocation().pathname.includes(RoutePaths.Creator);

    const forYou = {
        path: '/',
        text: t`For you`,
        icon: MusicNote,
    };

    const search = {
        path: `/${RoutePaths.Browse}`,
        text: t`Search`,
        icon: SearchOutlined,
    };

    const collections = {
        path: `/${RoutePaths.Collections}`,
        text: t`Collections`,
        icon: ArchiveOutlined,
    };

    const pluginCreator = {
        path: `/${RoutePaths.CreatorBeatMaker}`,
        title: CreatorBrand,
        icon: HeadphonesAddOutlined,
        isBrowserOnly: true,
        subTitle: t`Become a beat-making machine`,
        target: '_self',
    };

    const pluginKeyBpm = {
        path: `/${RoutePaths.KeyBpmFinder}`,
        title: t`Key & BPM Finder`,
        icon: SearchOutlined,
        isBrowserOnly: true,
        subTitle: t`Find the key and BPM of any song`,
        target: '_self',
    };

    const pluginSamples = {
        path: isAuthenticated
            ? `${CONFIG.VITE_LANDR_APP_DOMAIN_URL}/plugins/details/landr-samples`
            : `${CONFIG.VITE_GUESTSITE_URL}/plugins/landr-samples-plugin`,
        title: t`LANDR Samples`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        subTitle: t`Browse for the perfect samples for your track and pull them right in your DAW`,
        target: '_blank',
    };

    const pluginChromatic = {
        path: isAuthenticated
            ? `${CONFIG.VITE_LANDR_APP_DOMAIN_URL}/plugins/details/chromatic`
            : `${CONFIG.VITE_GUESTSITE_URL}/plugins/chromatic`,
        title: `LANDR Chromatic`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        subTitle: t`Explore new creative territories with a loop-based instrument plugin`,
        target: '_blank',
    };

    const pluginSampler = {
        path: isAuthenticated
            ? `${CONFIG.VITE_LANDR_APP_DOMAIN_URL}/plugins/details/landr-sampler`
            : `${CONFIG.VITE_GUESTSITE_URL}/plugins/landr-sampler`,
        title: `LANDR Sampler`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        subTitle: t`An intelligent tool that scans, organizes, and surfaces your best sounds`,
        target: '_blank',
    };

    const landrPlugins = [pluginSamples, pluginChromatic, pluginSampler];

    const plugins = {
        path: CONFIG.VITE_SAMPLES_PLUGIN_URL,
        text: t`Tools`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        target: '_blank',
        pluginsDropdownItemsPart1: [pluginCreator, pluginKeyBpm],
        pluginsDropdownItemsPart2: showLandrPlugins ? landrPlugins : [],
    };

    const getPublicNavigation = (handleClick: () => void) => {
        const isMobile = useBreakpoint('md');

        const routes: Route[] = [forYou, search, collections, plugins];

        return routes
            .filter(({ isBrowserOnly }) =>
                isDAWPlugin ? !isBrowserOnly : true,
            )
            .map((route) => {
                const isDropdown =
                    (route.pluginsDropdownItemsPart1 &&
                        route.pluginsDropdownItemsPart1.length > 0) ||
                    (route.pluginsDropdownItemsPart2 &&
                        route.pluginsDropdownItemsPart2.length > 0);

                if (isDropdown) {
                    return (
                        <Nav.Dropdown
                            key={route.text}
                            triggerEvent="hover"
                            width={{ base: '100%', md: '350px' }}
                            usePortal={true}
                        >
                            <Nav.DropdownTrigger paddingLeft="2xs" width="100%">
                                {isMobile && route.icon ? (
                                    <Text
                                        display="flex"
                                        alignItems="center"
                                        justifyContent={'center'}
                                        height="27px"
                                        width="100%"
                                    >
                                        <route.icon />
                                        <Box
                                            as="span"
                                            paddingLeft="xs"
                                            margin="0"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            width="88vw"
                                        >
                                            {route.text}
                                            <CaretRightOutlined />
                                        </Box>
                                    </Text>
                                ) : (
                                    <Box as="span" padding="0" margin="0">
                                        <Nav.Link
                                            padding="0"
                                            isCurrent={isToolActive}
                                        >
                                            {route.text}
                                        </Nav.Link>
                                    </Box>
                                )}
                            </Nav.DropdownTrigger>
                            <Nav.DropdownContent {...{ offset: [0, 8] }}>
                                {route.pluginsDropdownItemsPart1 &&
                                    route.pluginsDropdownItemsPart1.length >
                                        0 &&
                                    route.pluginsDropdownItemsPart1.map(
                                        (item: DropdownItemsProps) => (
                                            <NavigationDropdownItem
                                                key={item.title}
                                                icon={item.icon}
                                                title={item.title}
                                                subTitle={item.subTitle}
                                                onClick={handleClick}
                                                path={item.path}
                                                target={item?.target}
                                            />
                                        ),
                                    )}
                                {route.pluginsDropdownItemsPart2 &&
                                    route.pluginsDropdownItemsPart2.length >
                                        0 && (
                                        <>
                                            <Box padding="md">
                                                <Divider align="start">
                                                    Plugins
                                                </Divider>
                                            </Box>
                                            {route.pluginsDropdownItemsPart2.map(
                                                (item: DropdownItemsProps) => (
                                                    <NavigationDropdownItem
                                                        key={item.title}
                                                        icon={item.icon}
                                                        title={item.title}
                                                        subTitle={item.subTitle}
                                                        onClick={handleClick}
                                                        path={item.path}
                                                        target={item?.target}
                                                    />
                                                ),
                                            )}
                                        </>
                                    )}
                            </Nav.DropdownContent>
                        </Nav.Dropdown>
                    );
                }

                return (
                    <Nav.Link
                        key={route.text}
                        as={RouterLink}
                        to={route.path}
                        icon={isMobile && <route.icon />}
                        onClick={handleClick}
                        isCurrent={getIsCurrentPath(route.path, basename)}
                        target={route?.target}
                    >
                        {route.text}
                    </Nav.Link>
                );
            });
    };

    return getPublicNavigation;
};
